import { useCallback, useState } from 'react';
import type { GetPresignedUrlParams } from '@/types/fileManagerApiTypes';
import { useFetch } from '@/hooks/useFetch';
import type { ConfirmFileUploadRequest, ConfirmFileUploadResponse, GetPresignedUrlResponse } from '@/types/galleryApiTypes';

interface UploadFileDataResult {
  gallery_id: number;
  fileExtension: string;
  image_url: string;
}

interface UseUploadFileDataReturn {
  uploadFileData: (file: File) => Promise<UploadFileDataResult>;
  isLoading: boolean;
  error: unknown;
}

export const useUploadFileToGallery = (): UseUploadFileDataReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const { doFetch } = useFetch<GetPresignedUrlParams, GetPresignedUrlResponse>();
  const { doFetch: confirmFileUpload } = useFetch<ConfirmFileUploadRequest, ConfirmFileUploadResponse>();

  const getPresignedUrl = useCallback(
    async (fileExtension: string) => {
      return doFetch(`/api/gallery/presigned-url?file_extension=${fileExtension}`, {
        method: 'GET',
      }).then((resp) => {
        if (!resp?.data?.key || !resp?.data?.url) {
          throw new Error('Failed to get presigned URL');
        }
        return resp.data;
      }).catch((err) => {
        throw err;
      });
    },
    [doFetch]
  );

  const putToStorage = useCallback(
    async (url: string, file: File) => {
      const putResp = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type,
        },
        body: file,
      });
      if (!putResp.ok) {
        throw new Error('Failed to upload file to storage');
      }
    },
    []
  );

  const notifyFileUploaded = useCallback(
    async (params: ConfirmFileUploadRequest) => {
      return confirmFileUpload('/api/gallery', {
        method: 'POST',
        body: params,
      });
    },
    [confirmFileUpload]
  );

  const uploadFileData = useCallback(
    async (file: File): Promise<UploadFileDataResult> => {
      setIsLoading(true);
      setError(null);
      try {
        const fileExtension = file.name.split('.').pop() || '';
        const presignedData = await getPresignedUrl(fileExtension);
        await putToStorage(presignedData.url, file);
        const response = await notifyFileUploaded({
          key: presignedData.key,
          title: file.name,
        });

        return {
          gallery_id: response.data.id,
          fileExtension: fileExtension,
          image_url: presignedData.url.split('?')[0],
        };
      } catch (err) {
        setError(err);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [getPresignedUrl, putToStorage, notifyFileUploaded]
  );

  return { uploadFileData, isLoading, error };
};